import { type ReactNode } from "react";
import { ApiError } from "../lib/api";

type ErrorBannerProps = {
  /** Error to show; nothing renders when null */
  error: unknown;
};

/** Red box with an API or network error. */
export const ErrorBanner = ({ error }: ErrorBannerProps) => {
  if (!error) {
    return null;
  }
  const message = error instanceof Error ? error.message : String(error);
  const details = error instanceof ApiError ? error.details : undefined;
  return (
    <div
      role="alert"
      className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
    >
      <p className="font-medium">{message}</p>
      {details?.length ? (
        <ul className="mt-1 list-disc pl-5">
          {details.map((detail) => (
            <li key={detail}>{detail}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

/** Muted "Loading…" line. */
export const Loading = () => (
  <p className="py-8 text-center text-sm text-slate-500">Loading…</p>
);

type EmptyStateProps = {
  /** Heading */
  title: string;
  /** Explanation or action */
  children?: ReactNode;
};

/** Placeholder for an empty list. */
export const EmptyState = ({ title, children }: EmptyStateProps) => (
  <div className="px-6 py-12 text-center">
    <p className="text-sm font-medium text-slate-700">{title}</p>
    {children ? (
      <div className="mt-2 text-sm text-slate-500">{children}</div>
    ) : null}
  </div>
);
